import * as React from "react";
import {
  Box,
  Paper,
  Grid,
  TextField,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useState, useEffect, useRef } from "react";
import emailjs from "@emailjs/browser";

export default function ContactForm({ isSmallScreen, innerRef }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [tel, setTel] = useState("");
  const [message, setMessage] = useState("");

  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);

  const nameRef = useRef();
  const emailRef = useRef();
  const messageRef = useRef();

  // automatische Ausblendung der Alerts
  useEffect(() => {
    if (showSuccess || showFailure) {
      const timer = setTimeout(() => {
        setShowSuccess(false);
        setShowFailure(false);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess, showFailure]);

  // Fokus auf erstes fehlerhaftes Feld, wenn Validierung fehlschlägt
  useEffect(() => {
    if (showFailure) {
      if (!name) {
        nameRef.current.focus();
      } else if (!email) {
        emailRef.current.focus();
      } else if (!message) {
        messageRef.current.focus();
      }
    }
  }, [showFailure]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // einfache Validierung
    if (!name || !email || !message) {
      setShowFailure(true);
      return;
    }
    setShowConsentDialog(true);
  };

  const sendMessage = () => {
    setShowConsentDialog(false);
    const formData = {
      from_name: name,
      from_email: email,
      from_tel: tel,
      message,
    };
    emailjs
      .send(
        "service_y76xw9f",
        "template_llqdr1i",
        formData,
        "7eXZ-We0hTjg6bmmp"
      )
      .then(
        () => {
          setShowSuccess(true);
          // zurücksetzen
          setName("");
          setEmail("");
          setTel("");
          setMessage("");
          setConsentGiven(false);
        },
        () => setShowFailure(true)
      );
  };

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      ref={innerRef}
      elevation={4}
      aria-labelledby="contactform-title"
      sx={{
        p: { xs: 2, md: 4 },
        maxWidth: "100%",
        mx: "auto",
        backgroundColor: "background.paper",
      }}
    >
      <Typography id="contactform-title" variant="h5" mb={2}>
        Kontaktieren Sie uns:
      </Typography>

      {showSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} role="status">
          Vielen Dank! Wir haben Ihre Nachricht erhalten und melden uns zeitnah.
        </Alert>
      )}
      {showFailure && (
        <Alert severity="error" sx={{ mb: 2 }} role="alert">
          Fehler! Bitte füllen Sie alle Pflichtfelder korrekt aus.
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Name */}
        <Grid item xs={12} md={6}>
          <TextField
            id="contact-name"
            label="Ihr Name"
            variant="filled"
            fullWidth
            required
            inputRef={nameRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-required="true"
            aria-invalid={!!(showFailure && !name)}
          />
        </Grid>

        {/* E-Mail */}
        <Grid item xs={12} md={6}>
          <TextField
            id="contact-email"
            label="E-Mail-Adresse"
            variant="filled"
            type="email"
            fullWidth
            required
            inputRef={emailRef}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-required="true"
            aria-invalid={!!(showFailure && !email)}
          />
        </Grid>

        {/* Telefon */}
        <Grid item xs={12} md={6}>
          <TextField
            id="contact-tel"
            label="Telefon (optional)"
            variant="filled"
            type="tel"
            fullWidth
            value={tel}
            onChange={(e) => setTel(e.target.value)}
          />
        </Grid>

        {/* Nachricht */}
        <Grid item xs={12}>
          <TextField
            id="contact-message"
            label="Ihre Nachricht"
            variant="filled"
            fullWidth
            required
            multiline
            rows={6}
            inputRef={messageRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            aria-required="true"
            aria-invalid={!!(showFailure && !message)}
          />
        </Grid>

        {/* Einwilligung */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                id="contact-consent"
                checked={consentGiven}
                onChange={(e) => setConsentGiven(e.target.checked)}
                aria-required="true"
              />
            }
            label="Ich willige in die Verarbeitung meiner Daten ein."
          />
        </Grid>

        {/* Absenden */}
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SendIcon />}
            disabled={!consentGiven}
            size={isSmallScreen ? "medium" : "large"}
            aria-disabled={!consentGiven}
          >
            Nachricht senden
          </Button>
        </Grid>
      </Grid>

      {/* Consent Dialog */}
      <Dialog
        open={showConsentDialog}
        onClose={() => setShowConsentDialog(false)}
        aria-labelledby="consent-dialog-title"
        aria-describedby="consent-dialog-description"
      >
        <DialogTitle id="consent-dialog-title">
          Einwilligung zur Datenverarbeitung
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="consent-dialog-description">
            Mit dem Absenden des Formulars erklären Sie sich einverstanden,
            dass Ihre Daten zur Bearbeitung dieser Anfrage verwendet werden.
            Sie können Ihre Einwilligung jederzeit widerrufen.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConsentDialog(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={sendMessage}
            variant="contained"
            autoFocus
            aria-label="Einverstanden und Nachricht senden"
          >
            Einverstanden & Senden
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
